import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Result } from 'antd';

import SharedViewPage from './SharedViewPage';
import SharedFormPage from './SharedFormPage';
import { PublicViewSkeleton } from '../components/common/PublicPageSkeleton';
import { DataState } from '../components/loading';
import { resolveShortLink } from '../services/shortLinks';
import { getSharedView } from '../services/shareLinks';
import { isTokenValid } from '../utils';
import { clearSessionToken, getSessionToken } from '../utils/session';

/**
 * `/s/:code` — the one resolver for every compact keepr link.
 *
 * Two kinds of record answer to the same URL shape, and the resolver tells
 * them apart by the code's LENGTH, before it asks anything:
 *
 *   1. a SHORT LINK (7 chars) — navigation data for a signed-in user: a
 *      filter view, or any in-app path. `GET /api/short-links/{code}`
 *      requires a token, and every destination it names needs a session
 *      anyway, so a signed-out visitor with one is sent to sign in and back
 *      — and so is one whose token the lookup refuses (401), the token
 *      forgotten first.
 *   2. a SHARE LINK (22 chars) — a temporary public window onto some items,
 *      or (kind 'intake') a public FORM that creates one. `GET
 *      /api/share/{code}` takes NO token and answers every failure with one
 *      flat 404. A share-length code is ONLY ever looked up there, signed in
 *      or not, and a failure is the dead-link page — never the sign-in page.
 *
 * Why by length, and not "try the short link, then the share": the order
 * used to depend on the session, and the signed-out branch ended in a sign-in
 * redirect when the share lookup failed — "the one case where signing in
 * could help". It never could: no short link is longer than 7 characters, so
 * a dead share code sent a visitor to a login page that led back to the same
 * dead link (the live pass: an intake form switched off, reloaded signed out,
 * bounced to /login). A signed-in reader with a revoked token fared no better
 * the other way round — the short-link lookup 401'd and a live share link
 * read as missing. The public lookup needs no session, so a public code
 * never touches one.
 */

// Short-link codes are 7 base62 characters (keepr-api utils/shortLinkUtils.js
// CODE_LENGTH); share-link codes are 22 (utils/capabilityCode.js). Anything
// longer than a short link can only be a share code.
export const SHARE_CODE_MIN_LENGTH = 11;

export const INVALID_LINK_TITLE = "This link doesn't exist";

function InvalidLink({ onHome }) {
	return (
		<Result
			className="short-link__invalid"
			status="404"
			title={INVALID_LINK_TITLE}
			subTitle="It may have expired, been revoked, or been mistyped. Ask whoever shared it for a new one."
			extra={<Button type="primary" onClick={onHome}>Go to keepr</Button>}
		/>
	);
}

export const FORM_OUT_OF_DATE_TITLE = 'This form needs updating by its owner';

/**
 * An intake form whose link no longer matches its card (400
 * `form_out_of_date`): the link is alive, so this is NOT the dead-link page —
 * the owner can fix it, and the visitor can come back.
 */
function FormUnavailable({ onHome }) {
	return (
		<Result
			className="short-link__invalid"
			status="warning"
			title={FORM_OUT_OF_DATE_TITLE}
			subTitle="Try again later, or tell whoever sent you the link."
			extra={<Button onClick={onHome}>Go to keepr</Button>}
		/>
	);
}

/** Is this the one live-but-unservable answer an intake code can give? */
export const isFormOutOfDate = error => error?.response?.status === 400
	&& error?.response?.data?.code === 'form_out_of_date';

/** A filter descriptor → the items-page URL it stands for. */
export function buildFilterPath({ collection_id, query, sort_field, sort_direction, group_by }) {
	const search = [
		query ? `q=${encodeURIComponent(query)}` : null,
		sort_field ? `sort=${encodeURIComponent(sort_field)}` : null,
		sort_direction ? `dir=${encodeURIComponent(sort_direction)}` : null,
		// The grid's separator period. The API emits null for a link that
		// stored none; a stored one is an instruction and is passed through.
		group_by ? `group=${encodeURIComponent(group_by)}` : null,
	].filter(Boolean).join('&');
	return `/collections/${collection_id}/items${search ? `?${search}` : ''}`;
}

/**
 * Search engines (PR 10c; the plan's §15.7): a capability URL is found only if
 * someone posts it publicly, and then a crawler could index the page — and,
 * with files on public links, its photos. Every page this resolver shows
 * carries `<meta name="robots" content="noindex">` while it is up (the
 * public byte routes send `X-Robots-Tag` themselves), and none sets
 * `og:image`: a link unfurled in a chat app must not hand a photo to the
 * unfurler.
 */
export function useNoIndex() {
	useEffect(() => {
		const meta = document.createElement('meta');
		meta.setAttribute('name', 'robots');
		meta.setAttribute('content', 'noindex');
		document.head.appendChild(meta);
		return () => { meta.remove(); };
	}, []);
}

export default function ShortLinkPage() {
	const { code } = useParams();
	const navigate = useNavigate();
	useNoIndex();
	// 'loading' | 'shared' | 'missing' | 'out-of-date'
	const [status, setStatus] = useState('loading');
	const [sharedView, setSharedView] = useState(null);
	const [attempt, setAttempt] = useState(0);

	const handleDead = useCallback(() => {
		setSharedView(null);
		setStatus('missing');
	}, []);

	const handleOutOfDate = useCallback(() => {
		setSharedView(null);
		setStatus('out-of-date');
	}, []);

	useEffect(() => {
		if (!code) { setStatus('missing'); return undefined; }
		let active = true;

		const isShareCode = code.length >= SHARE_CODE_MIN_LENGTH;

		// A share code: the public lookup and nothing else. Every refusal —
		// unknown, expired, revoked, an intake whose collection switched its
		// forms off — is the dead-link page, whoever is looking.
		if (isShareCode) {
			getSharedView(code)
				.then(view => {
					if (!active) return;
					setSharedView(view);
					setStatus('shared');
				})
				.catch((error) => {
					if (!active) return;
					setStatus(isFormOutOfDate(error) ? 'out-of-date' : 'missing');
				});
			return () => { active = false; };
		}

		// A short link is a signed-in user's navigation: with no session, sign
		// in and come back to it (an in-app hop — same origin, never off it).
		const signInAndReturn = () => navigate(
			`/login?isRedirect=true&returnPath=${encodeURIComponent(`/s/${code}`)}`,
			{ replace: true }
		);
		if (!isTokenValid()) {
			signInAndReturn();
			return () => { active = false; };
		}

		const sentWith = getSessionToken();
		resolveShortLink(code)
			.then(descriptor => {
				if (!active) return;
				if (descriptor?.kind === 'path' && descriptor.path) {
					navigate(descriptor.path, { replace: true });
					return;
				}
				if (descriptor?.kind === 'filter' && descriptor.collection_id) {
					navigate(buildFilterPath(descriptor), { replace: true });
					return;
				}
				setStatus('missing');
			})
			.catch((error) => {
				if (!active) return;
				// A 401 is about the SESSION, not the link: a token the server
				// no longer accepts (signed out everywhere, revoked) that has
				// not yet expired here. Reading it as "This link doesn't exist"
				// (the review of the live-pass merges) told the person a live
				// link was dead. Forget the token — only if it is still the one
				// this lookup sent: a sign-in in another tab since is not
				// refused — and sign in and back, as a visitor with none would.
				// (The api client leaves /s/ alone: it owns its signed-out state.)
				if (error?.response?.status === 401) {
					const current = getSessionToken();
					if (current && current === sentWith) clearSessionToken();
					signInAndReturn();
					return;
				}
				// Unknown, or refused for any other reason: the dead-link page.
				setStatus('missing');
			});

		return () => { active = false; };
	}, [code, navigate, attempt]);

	const renderOutcome = () => {
		if (status === 'shared' && sharedView) {
			// An INTAKE link is a form, not a window: it writes, and it shows the
			// visitor nothing of the collection. It gets its own page rather than
			// a branch inside the read page, so nothing the read page knows how to
			// render can leak into it.
			if (sharedView.kind === 'intake') {
				return <SharedFormPage code={code} view={sharedView} onDead={handleDead} onOutOfDate={handleOutOfDate} />;
			}
			return <SharedViewPage code={code} view={sharedView} onDead={handleDead} />;
		}
		if (status === 'out-of-date') return <FormUnavailable onHome={() => navigate('/')} />;
		return <InvalidLink onHome={() => navigate('/')} />;
	};

	// The wait for the lookup is the standard loading-initial state: nothing
	// for the first moments, then the page's skeleton, then (after 30 s) the
	// error with Retry, so a link that never answers is never a perpetual
	// skeleton. Every refusal is its own screen above, not a skeleton.
	const waiting = status === 'loading';
	return (
		<DataState
			// A retry starts the wait afresh, with its own 30 s.
			key={attempt}
			state={waiting ? 'loading-initial' : 'ready'}
			skeleton={<PublicViewSkeleton />}
			onRetry={() => { setStatus('loading'); setAttempt(n => n + 1); }}
			label="this link"
			className="data-state--public"
		>
			{waiting ? null : renderOutcome()}
		</DataState>
	);
}
